/**
 * views/Kitchen/KitchenStations.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Station setup: the grill, the fryer, the drinks fridge, the pass, and the
 * rules that decide which menu item lands on which board.
 *
 * ## Why this screen is light and the board is dark
 *
 * views/Kitchen/kitchenUi.js is a dark, high-chroma palette because a board is
 * read from three metres away over a hot pass. THIS screen is configuration:
 * it is read at a desk, next to the menu screen, by someone with a mouse and
 * time to think. It therefore follows components/dashboardTheme.js like every
 * other admin page, and borrows only `stationColour` from the kitchen tokens
 * so a station wears the same colour here as it does on the board.
 *
 * ## The preview is the point of the screen
 *
 * A keyword is a blast radius, not a setting. "chip" on the fryer quietly
 * claims "Chipsi Mayai" and "Chocolate Chip Cake" as well, and nobody finds
 * out until a Friday night. So the preview runs the REAL router
 * (services/kitchen/stationRouter.js) over the REAL menu and reports, per
 * item, which rule claimed it. It can also be run against UNSAVED edits from
 * inside the modal, which is the one moment the answer is still cheap to act
 * on.
 *
 * @see constants/kitchen.js               STATION_MATCH_PRECEDENCE, STARTER_STATIONS
 * @see services/kitchen/stationRouter.js  resolveStationForItem, previewRouting
 * @see controllers/kitchenController.js   listKitchenStations, previewStationRouting
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
  Spinner,
} from "react-bootstrap";
import { useHistory, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import {
  FiEdit2,
  FiInfo,
  FiPlus,
  FiRefreshCw,
  FiTrash2,
  FiX,
} from "react-icons/fi";

import {
  apiKitchenCreateStation,
  apiKitchenList,
  apiKitchenDeleteStation,
  apiKitchenPreviewRouting,
  apiKitchenSeedStations,
  apiKitchenStations,
  apiKitchenUpdateStation,
} from "../../api/index";
import { THEME } from "../../components/dashboardTheme";
import { stationColour } from "./kitchenUi";
import AppSelect from "../../components/ui/AppSelect";

/**
 * constants/kitchen.js sits above frontend/src and CRA's ModuleScopePlugin
 * refuses any import that reaches outside src/ — which is precisely why
 * kitchenUi.js restates its display tokens rather than importing them. Only
 * the two values this screen genuinely reasons about are mirrored here, and
 * both are load-bearing on the server: FALLBACK_STATION_CODE is what the
 * delete endpoint protects, and STATION_MATCH_SOURCE is the vocabulary the
 * preview answers in.
 */
const FALLBACK_STATION_CODE = "MAIN";

/**
 * How the preview explains itself. `matchedOn` is a keyword for KEYWORD and an
 * id for the three pinning tiers, so ids are shown as a short tail rather than
 * a wall of hex: enough to line up against the menu screen, short enough to
 * read down a column.
 */
const SOURCE_META = {
  PRODUCT: { label: "product pin", showsId: true },
  SECTION: { label: "menu section", showsId: true },
  CATEGORY: { label: "category", showsId: true },
  KEYWORD: { label: "keyword", showsId: false },
  DEFAULT: { label: "default station", showsId: false },
  FALLBACK: { label: "no rule matched", showsId: false },
  MANUAL: { label: "moved by hand", showsId: false },
};

/** Same hues the seeded starter stations use, so a hand-made one fits in. */
const COLOUR_PRESETS = [
  "#f97316",
  "#eab308",
  "#22c55e",
  "#38bdf8",
  "#a78bfa",
  "#ec4899",
  "#14b8a6",
  "#94a3b8",
];

const readError = (err, fallback) => err?.response?.data?.message || fallback;
const readErrorCode = (err) => err?.response?.data?.code || null;

const shortId = (value) => {
  const text = String(value || "");
  return text.length > 6 ? text.slice(-6) : text;
};

/**
 * Live routing runs the station list through `stationsForLocation`, which drops
 * anything switched off. The preview endpoint does NOT filter, so sending the
 * raw list would draw a column for a station that cannot receive a single item
 * in production. Filtering here keeps the preview honest about what would
 * actually happen tonight.
 */
const routableStations = (list) => (list || []).filter((s) => s?.isActive !== false);

/**
 * The server rebuilds the whole station document from the request body, so an
 * omitted array is a CLEARED array. Product, section and category pins are
 * edited on the menu screen; every write from this screen must carry them
 * through untouched or renaming a station would silently unpin its menu.
 */
const stationToBody = (station, patch = {}) => {
  const rawSort = station?.sortOrder;
  const parsedSort = Number(rawSort);
  const sortOrder =
    String(rawSort ?? "").trim() === "" || !Number.isFinite(parsedSort) ? 100 : parsedSort;

  return {
    code: String(station?.code || "").trim().toUpperCase(),
    name: String(station?.name || "").trim(),
    colour: station?.colour || "#94a3b8",
    sortOrder,
    isActive: station?.isActive !== false,
    isDefault: !!station?.isDefault,
    keywords: station?.keywords || [],
    productIds: station?.productIds || [],
    sectionIds: station?.sectionIds || [],
    categoryIds: station?.categoryIds || [],
    autoAdvanceOnAllDone: !!station?.autoAdvanceOnAllDone,
    ...(station?.restaurantId ? { restaurantId: station.restaurantId } : {}),
    ...(station?.outletId ? { outletId: station.outletId } : {}),
    ...patch,
  };
};

const emptyStation = () => ({
  _id: null,
  code: "",
  name: "",
  colour: "#38bdf8",
  sortOrder: 100,
  isActive: true,
  isDefault: false,
  keywords: [],
  productIds: [],
  sectionIds: [],
  categoryIds: [],
});

/** "3 products, 1 section, 5 keywords" — the card-sized version of the rules. */
const ruleSummary = (station) => {
  const counted = [
    [(station?.productIds || []).length, "product", "products"],
    [(station?.sectionIds || []).length, "section", "sections"],
    [(station?.categoryIds || []).length, "category", "categories"],
    [(station?.keywords || []).length, "keyword", "keywords"],
  ]
    .filter(([n]) => n > 0)
    .map(([n, one, many]) => `${n} ${n === 1 ? one : many}`);

  if (counted.length) return counted.join(", ");
  if (station?.isDefault) return "No rules of its own. It catches whatever nothing else claims.";
  return "No rules yet, so nothing routes here.";
};

const isFallbackStation = (station) =>
  String(station?.code || "").toUpperCase() === FALLBACK_STATION_CODE;

// ── Small presentational pieces ──────────────────────────────────────────────
function Pill({ children, background, colour, title }) {
  return (
    <span
      title={title}
      style={{
        display: "inline-block",
        padding: "2px 9px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: "0.3px",
        textTransform: "uppercase",
        background,
        color: colour,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

function Swatch({ colour, size = 14 }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: size,
        height: size,
        borderRadius: 4,
        background: colour,
        border: `1px solid ${THEME.border}`,
        flex: "0 0 auto",
      }}
    />
  );
}

function PanelHeading({ title, subtitle, action }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        gap: 12,
        padding: "14px 18px",
        borderBottom: `1px solid ${THEME.border}`,
        background: THEME.stripBg,
      }}
    >
      <div>
        <div
          style={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: "0.6px",
            textTransform: "uppercase",
            color: THEME.text,
          }}
        >
          {title}
        </div>
        {subtitle ? (
          <div style={{ fontSize: 12.5, color: THEME.textMid, marginTop: 4 }}>{subtitle}</div>
        ) : null}
      </div>
      {action}
    </div>
  );
}

// ── Keyword chips ────────────────────────────────────────────────────────────
/**
 * Built inline rather than pulled from a component library: the dashboard has
 * no shared tags input, and a chip list is twenty lines of markup against a
 * new dependency and a new bug surface.
 *
 * Keywords are lower-cased here because the server lower-cases them anyway.
 * Normalising at the point of entry means the chip a manager sees is exactly
 * the string that will be matched, not a prettier version of it.
 */
function KeywordField({ keywords, onChange, disabled }) {
  const [draft, setDraft] = useState("");

  const add = () => {
    const next = String(draft).trim().toLowerCase();
    setDraft("");
    if (!next || keywords.includes(next)) return;
    onChange([...keywords, next]);
  };

  const remove = (word) => onChange(keywords.filter((k) => k !== word));

  const onKeyDown = (event) => {
    if (event.key === "Enter" || event.key === ",") {
      // Enter inside a modal would otherwise submit the form and save a
      // half-typed keyword as the whole edit.
      event.preventDefault();
      add();
      return;
    }
    if (event.key === "Backspace" && !draft && keywords.length) {
      onChange(keywords.slice(0, -1));
    }
  };

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 6,
          minHeight: 38,
          alignItems: "center",
          padding: "6px 8px",
          border: `1px solid ${THEME.border}`,
          borderRadius: THEME.radiusSm,
          background: disabled ? THEME.stripBg : "#fff",
        }}
      >
        {keywords.map((word) => (
          <span
            key={word}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "3px 8px",
              borderRadius: 999,
              fontSize: 12,
              fontWeight: 600,
              background: THEME.blueSoft,
              color: THEME.blueDeep,
            }}
          >
            {word}
            <button
              type="button"
              onClick={() => remove(word)}
              disabled={disabled}
              aria-label={`Remove keyword ${word}`}
              style={{
                border: "none",
                background: "transparent",
                padding: 0,
                lineHeight: 1,
                color: THEME.blueDeep,
                cursor: "pointer",
              }}
            >
              <FiX size={12} />
            </button>
          </span>
        ))}
        <input
          type="text"
          value={draft}
          disabled={disabled}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={add}
          placeholder={keywords.length ? "Add another" : "chips, wings, fried"}
          style={{
            flex: "1 1 120px",
            minWidth: 120,
            border: "none",
            outline: "none",
            fontSize: 13.5,
            background: "transparent",
            color: THEME.text,
          }}
        />
      </div>
      <div style={{ fontSize: 12, color: THEME.textLight, marginTop: 6 }}>
        Press Enter to add each keyword. Matching is whole word and ignores case, so
        &quot;chips&quot; claims &quot;Fish and Chips&quot; but not &quot;Chipsi Mayai&quot;.
      </div>
    </div>
  );
}

// ── Create / edit ────────────────────────────────────────────────────────────
/**
 * Mounted with a `key` of the station id, so opening a different station
 * remounts the modal with fresh state. That is deliberately cheaper and safer
 * than an effect that copies props into state: there is no window in which the
 * form shows one station's values under another station's title.
 */
function StationFormModal({ station, saving, onCancel, onSave, onPreviewDraft }) {
  const [form, setForm] = useState(() => ({ ...emptyStation(), ...(station || {}) }));
  const isEditing = !!form._id;

  const set = (field) => (event) => {
    const raw = event.target;
    const value = raw.type === "checkbox" ? raw.checked : raw.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const codeInvalid = !String(form.code || "").trim();
  const nameInvalid = !String(form.name || "").trim();
  const blocked = codeInvalid || nameInvalid || saving;

  return (
    <Modal show onHide={onCancel} size="lg" backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {isEditing ? `Edit ${form.name || "station"}` : "New station"}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="g-3">
          <Col md={7}>
            <Form.Group>
              <Form.Label style={{ fontSize: 12.5, fontWeight: 700, color: THEME.textMid }}>
                Name
              </Form.Label>
              <Form.Control
                type="text"
                value={form.name || ""}
                onChange={set("name")}
                isInvalid={nameInvalid}
                placeholder="Grill"
                maxLength={60}
              />
              <Form.Control.Feedback type="invalid">
                Give the station a name the kitchen would actually say out loud.
              </Form.Control.Feedback>
            </Form.Group>
          </Col>

          <Col md={5}>
            <Form.Group>
              <Form.Label style={{ fontSize: 12.5, fontWeight: 700, color: THEME.textMid }}>
                Code
              </Form.Label>
              <Form.Control
                type="text"
                value={form.code || ""}
                disabled={isEditing}
                maxLength={24}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))
                }
                isInvalid={codeInvalid}
                placeholder="GRILL"
              />
              <Form.Control.Feedback type="invalid">A code is required.</Form.Control.Feedback>
              <div style={{ fontSize: 12, color: THEME.textLight, marginTop: 6 }}>
                {isEditing
                  ? "The code is fixed once tickets have been cooked under it. Historic tickets keep the code they were routed with, so changing it would break the join between them."
                  : "Short, uppercase, unique to this kitchen. This is what the board prints on a ticket line."}
              </div>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label style={{ fontSize: 12.5, fontWeight: 700, color: THEME.textMid }}>
                Colour
              </Form.Label>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Form.Control
                  type="color"
                  value={form.colour || "#94a3b8"}
                  onChange={set("colour")}
                  title="Pick a station colour"
                  style={{ width: 54, padding: 4 }}
                />
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {COLOUR_PRESETS.map((preset) => (
                    <button
                      key={preset}
                      type="button"
                      aria-label={`Use colour ${preset}`}
                      onClick={() => setForm((prev) => ({ ...prev, colour: preset }))}
                      style={{
                        width: 22,
                        height: 22,
                        borderRadius: 6,
                        background: preset,
                        cursor: "pointer",
                        border:
                          String(form.colour).toLowerCase() === preset
                            ? `2px solid ${THEME.text}`
                            : `1px solid ${THEME.border}`,
                      }}
                    />
                  ))}
                </div>
              </div>
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Group>
              <Form.Label style={{ fontSize: 12.5, fontWeight: 700, color: THEME.textMid }}>
                Sort order
              </Form.Label>
              <Form.Control
                type="number"
                value={form.sortOrder ?? ""}
                onChange={set("sortOrder")}
                placeholder="100"
              />
              <div style={{ fontSize: 12, color: THEME.textLight, marginTop: 6 }}>
                Lower numbers come first, both on the board and when two stations claim the
                same item at the same tier.
              </div>
            </Form.Group>
          </Col>

          <Col md={12}>
            <Form.Group>
              <Form.Label style={{ fontSize: 12.5, fontWeight: 700, color: THEME.textMid }}>
                Keywords
              </Form.Label>
              <KeywordField
                keywords={form.keywords || []}
                disabled={saving}
                onChange={(keywords) => setForm((prev) => ({ ...prev, keywords }))}
              />
            </Form.Group>
          </Col>

          <Col md={6}>
            <Form.Check
              type="switch"
              id="station-active"
              checked={form.isActive !== false}
              onChange={set("isActive")}
              label="Active"
            />
            <div style={{ fontSize: 12, color: THEME.textLight, marginTop: 4 }}>
              A switched off station routes nothing. Tickets already cooked under it keep
              their history.
            </div>
          </Col>

          <Col md={6}>
            <Form.Check
              type="switch"
              id="station-default"
              checked={!!form.isDefault}
              onChange={set("isDefault")}
              label="Default station"
            />
            <div style={{ fontSize: 12, color: THEME.textLight, marginTop: 4 }}>
              Anything no rule claims lands here. Only one station can hold this, so turning
              it on takes it off whichever station has it now.
            </div>
          </Col>

          <Col md={12}>
            <div
              style={{
                marginTop: 4,
                padding: "10px 12px",
                borderRadius: THEME.radiusSm,
                background: THEME.blueSoft,
                color: THEME.textMid,
                fontSize: 12.5,
              }}
            >
              Pinning a product or a whole menu section to this station is done on the menu
              screen, not here. Those pins are carried through untouched when you save, so
              editing a name or a keyword never unpins anything.
            </div>
          </Col>
        </Row>
      </Modal.Body>

      <Modal.Footer style={{ justifyContent: "space-between" }}>
        <Button
          variant="outline-secondary"
          size="sm"
          disabled={saving || codeInvalid || nameInvalid}
          onClick={() => onPreviewDraft(form)}
        >
          Preview these rules without saving
        </Button>
        <div style={{ display: "flex", gap: 8 }}>
          <Button variant="light" onClick={onCancel} disabled={saving}>
            Cancel
          </Button>
          <Button variant="primary" disabled={blocked} onClick={() => onSave(form)}>
            {saving ? "Saving" : "Save station"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

// ── Routing preview ──────────────────────────────────────────────────────────
/**
 * One column per station, listing the live menu items that would land there
 * and the rule that claimed each of them.
 *
 * Columns are capped at MAX_PREVIEW_ROWS rendered rows. A thousand item menu
 * across six stations is a DOM nobody reads and a scroll nobody survives; the
 * count above each column is the real number and the cap says so out loud.
 */
const MAX_PREVIEW_ROWS = 60;

function PreviewColumn({ bucket, stations }) {
  const colour = bucket.colour || stationColour(bucket.code, stations);
  const items = bucket.items || [];
  const shown = items.slice(0, MAX_PREVIEW_ROWS);

  return (
    <div
      style={{
        flex: "0 0 260px",
        border: `1px solid ${THEME.border}`,
        borderRadius: THEME.radiusSm,
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        maxHeight: 460,
      }}
    >
      <div
        style={{
          padding: "10px 12px",
          borderBottom: `1px solid ${THEME.border}`,
          borderTop: `3px solid ${colour}`,
          borderTopLeftRadius: THEME.radiusSm,
          borderTopRightRadius: THEME.radiusSm,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Swatch colour={colour} />
          <span style={{ fontWeight: 700, fontSize: 13.5, color: THEME.text }}>
            {bucket.name || bucket.code}
          </span>
        </div>
        <div style={{ fontSize: 11.5, color: THEME.textLight, marginTop: 3 }}>
          {bucket.code} · {items.length} {items.length === 1 ? "item" : "items"}
        </div>
      </div>

      <div style={{ overflowY: "auto", padding: "6px 0" }}>
        {items.length === 0 ? (
          <div style={{ padding: "12px", fontSize: 12.5, color: THEME.textLight }}>
            Nothing routes here yet.
          </div>
        ) : null}

        {shown.map((item, index) => {
          const meta = SOURCE_META[item.source] || { label: String(item.source || "unknown") };
          const detail = meta.showsId
            ? `${meta.label} ${shortId(item.matchedOn)}`
            : item.source === "KEYWORD" && item.matchedOn
            ? `${meta.label} "${item.matchedOn}"`
            : meta.label;

          return (
            <div
              key={`${item.id || item.name}-${index}`}
              style={{
                padding: "7px 12px",
                borderBottom: `1px solid ${THEME.stripBg}`,
              }}
            >
              <div style={{ fontSize: 13, color: THEME.text, lineHeight: 1.3 }}>
                {item.name || "Unnamed item"}
              </div>
              <div style={{ fontSize: 11, color: THEME.textLight, marginTop: 2 }} title={String(item.matchedOn || "")}>
                {detail}
              </div>
            </div>
          );
        })}

        {items.length > shown.length ? (
          <div style={{ padding: "8px 12px", fontSize: 12, color: THEME.textMid }}>
            and {items.length - shown.length} more, not drawn here to keep the page usable.
          </div>
        ) : null}
      </div>
    </div>
  );
}

function RoutingPreview({
  preview,
  loading,
  error,
  stations,
  hiddenCount,
  everythingOff,
  onRefresh,
  onBackToDraft,
  onDiscardDraft,
}) {
  return (
    <Card style={{ border: `1px solid ${THEME.border}`, borderRadius: THEME.radius, boxShadow: THEME.shadow }}>
      <PanelHeading
        title="Preview routing"
        subtitle="Where today's menu would land if this configuration were live."
        action={
          <Button variant="outline-primary" size="sm" onClick={onRefresh} disabled={loading}>
            <FiRefreshCw size={13} style={{ marginRight: 6, verticalAlign: "-1px" }} />
            {loading ? "Working" : "Run preview"}
          </Button>
        }
      />

      <Card.Body>
        {/* Stated once, here, because every question about the preview is
            really a question about precedence. */}
        <div
          style={{
            display: "flex",
            gap: 10,
            padding: "12px 14px",
            borderRadius: THEME.radiusSm,
            background: THEME.blueSoft,
            color: THEME.textMid,
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          <FiInfo size={16} style={{ flex: "0 0 auto", marginTop: 2, color: THEME.blueDeep }} />
          <div>
            <strong style={{ color: THEME.text }}>How an item picks a station.</strong> A product
            pinned to a station wins first. If nothing is pinned, the item&apos;s menu section
            decides. Then its category. Then a keyword in its name. Anything still unclaimed goes
            to the default station. The first rule that matches wins, and nothing further down the
            list can take the item back.
          </div>
        </div>

        {preview?.unsaved ? (
          <div
            style={{
              marginTop: 14,
              padding: "12px 14px",
              borderRadius: THEME.radiusSm,
              background: THEME.amberSoft,
              border: `1px solid ${THEME.amber}`,
              fontSize: 13,
              color: THEME.textMid,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 10,
            }}
          >
            <span>
              This preview includes unsaved changes to{" "}
              <strong style={{ color: THEME.text }}>{preview.draftName || "a station"}</strong>.
              Nothing has been written yet.
            </span>
            <Button variant="outline-secondary" size="sm" onClick={onBackToDraft}>
              Back to editing
            </Button>
            <Button variant="link" size="sm" onClick={onDiscardDraft} style={{ color: THEME.red }}>
              Discard the draft
            </Button>
          </div>
        ) : null}

        {error ? (
          <div
            style={{
              marginTop: 14,
              padding: "12px 14px",
              borderRadius: THEME.radiusSm,
              background: THEME.redSoft,
              border: `1px solid ${THEME.red}`,
              color: THEME.red,
              fontSize: 13,
            }}
          >
            {error}
          </div>
        ) : null}

        {everythingOff ? (
          <div style={{ marginTop: 16, fontSize: 13.5, color: THEME.textMid }}>
            Every station is switched off, so there is nothing to route with. While it stays that
            way each item falls to the main line and the board shows a single lane.
          </div>
        ) : null}

        {loading && !preview ? (
          <div style={{ marginTop: 20, display: "flex", alignItems: "center", gap: 10 }}>
            <Spinner animation="border" size="sm" />
            <span style={{ fontSize: 13, color: THEME.textMid }}>Routing the menu.</span>
          </div>
        ) : null}

        {preview && !everythingOff ? (
          <>
            <div style={{ marginTop: 16, fontSize: 13, color: THEME.textMid }}>
              Previewed against <strong style={{ color: THEME.text }}>{preview.menuSize}</strong>{" "}
              live menu {preview.menuSize === 1 ? "item" : "items"}.
              {hiddenCount > 0 ? (
                <>
                  {" "}
                  {hiddenCount} switched off {hiddenCount === 1 ? "station is" : "stations are"} not
                  shown, because live routing skips them.
                </>
              ) : null}
            </div>

            {preview.truncated ? (
              <div
                style={{
                  marginTop: 10,
                  padding: "10px 12px",
                  borderRadius: THEME.radiusSm,
                  background: THEME.amberSoft,
                  border: `1px solid ${THEME.amber}`,
                  fontSize: 13,
                  color: THEME.textMid,
                }}
              >
                Only the first 1000 menu items were previewed. Anything past that is not in the
                columns below, so treat this as a sample rather than the whole menu.
              </div>
            ) : null}

            <div
              style={{
                display: "flex",
                gap: 12,
                marginTop: 16,
                overflowX: "auto",
                paddingBottom: 8,
              }}
            >
              {(preview.buckets || []).map((bucket) => (
                <PreviewColumn key={bucket.code} bucket={bucket} stations={stations} />
              ))}
            </div>
          </>
        ) : null}
      </Card.Body>
    </Card>
  );
}

// ── One station ──────────────────────────────────────────────────────────────
function StationCard({ station, stations, busy, onEdit, onDelete, onToggleActive }) {
  const colour = stationColour(station.code, stations);
  const off = station.isActive === false;

  return (
    <Card
      style={{
        height: "100%",
        border: `1px solid ${THEME.border}`,
        borderTop: `3px solid ${colour}`,
        borderRadius: THEME.radius,
        boxShadow: THEME.shadow,
        opacity: off ? 0.72 : 1,
      }}
    >
      <Card.Body style={{ padding: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <Swatch colour={colour} size={16} />
          <span style={{ fontWeight: 700, fontSize: 15.5, color: THEME.text }}>{station.name}</span>
          {station.isDefault ? (
            <Pill
              background={THEME.tealSoft}
              colour={THEME.teal}
              title="Anything no rule claims lands on this station."
            >
              Default station
            </Pill>
          ) : null}
          {isFallbackStation(station) ? (
            <Pill
              background={THEME.stripBg}
              colour={THEME.textMid}
              title="The main line always exists. It cannot be removed."
            >
              Main line
            </Pill>
          ) : null}
          {off ? (
            <Pill background={THEME.redSoft} colour={THEME.red}>
              Switched off
            </Pill>
          ) : null}
        </div>

        <div style={{ fontSize: 12, color: THEME.textLight, marginTop: 6 }}>
          {String(station.code).toUpperCase()} · sorts at {station.sortOrder ?? 100}
        </div>

        <div style={{ fontSize: 13, color: THEME.textMid, marginTop: 10, minHeight: 38 }}>
          {ruleSummary(station)}
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            marginTop: 12,
            paddingTop: 12,
            borderTop: `1px solid ${THEME.border}`,
          }}
        >
          <Form.Check
            type="switch"
            id={`station-active-${station._id}`}
            checked={!off}
            disabled={busy}
            onChange={() => onToggleActive(station)}
            label={<span style={{ fontSize: 12.5, color: THEME.textMid }}>Active</span>}
          />
          <div style={{ display: "flex", gap: 6 }}>
            <Button variant="outline-secondary" size="sm" onClick={() => onEdit(station)} disabled={busy}>
              <FiEdit2 size={13} style={{ verticalAlign: "-1px" }} />
              <span style={{ marginLeft: 6 }}>Edit</span>
            </Button>
            <Button variant="outline-danger" size="sm" onClick={() => onDelete(station)} disabled={busy}>
              <FiTrash2 size={13} style={{ verticalAlign: "-1px" }} />
            </Button>
          </div>
        </div>
      </Card.Body>
    </Card>
  );
}

// ── Screen ───────────────────────────────────────────────────────────────────
function KitchenStations() {
  const { search } = useLocation();
  const history = useHistory();

  /**
   * Lusuku staff have every kitchen and no way to guess an id, so the screen
   * loads the list and offers a selector. It previously told the operator to
   * hand-edit `?vendorId=` into the address bar, which is not a user interface.
   * A restaurant signing in with its own account has exactly one kitchen and
   * never sees the control.
   */
  const [kitchens, setKitchens] = useState([]);

  /**
   * Lusuku staff drive another restaurant's kitchen with ?vendorId=. A vendor's
   * own token already resolves to their own kitchen and the server IGNORES the
   * parameter for them (utils/kitchenAccess.js), so threading it unconditionally
   * is safe. Forgetting it on ONE call is how a preview quietly describes a
   * different restaurant's menu than the cards above it.
   */
  const vendorId = useMemo(() => new URLSearchParams(search).get("vendorId") || "", [search]);
  const params = useMemo(() => (vendorId ? { vendorId } : {}), [vendorId]);

  useEffect(() => {
    let cancelled = false;
    apiKitchenList()
      .then(({ data }) => {
        if (cancelled) return;
        const list = data?.data?.kitchens || [];
        setKitchens(list);
        // One kitchen means no choice to make. Select it so the screen works
        // immediately instead of refusing on a technicality.
        if (!vendorId && list.length === 1) {
          history.replace(
            `/admin/kitchen-stations?vendorId=${encodeURIComponent(String(list[0].vendorId))}`
          );
        }
      })
      .catch(() => {
        if (!cancelled) setKitchens([]);
      });
    return () => {
      cancelled = true;
    };
  }, [vendorId, history]);

  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [loadErrorCode, setLoadErrorCode] = useState(null);

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  const [editing, setEditing] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const [deleting, setDeleting] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleteBusy, setDeleteBusy] = useState(false);

  /**
   * The preview takes the list as an ARGUMENT rather than reading `stations`
   * from the closure. That is what lets the same function serve the saved list
   * and an unsaved draft, and it keeps the dependency array down to `params`
   * so nothing re-fires on every keystroke in the modal.
   */
  const runPreview = useCallback(
    async (list, { unsaved = false, draftName = null } = {}) => {
      const routable = routableStations(list);

      // An empty array makes the server fall back to the SAVED stations, which
      // would answer a question nobody asked. When every station is off, the
      // honest answer is the copy in RoutingPreview, not a request.
      if (!routable.length) {
        setPreview(null);
        setPreviewError(null);
        return;
      }

      setPreviewLoading(true);
      setPreviewError(null);
      try {
        const { data } = await apiKitchenPreviewRouting({ stations: routable }, params);
        const payload = data?.data || {};
        setPreview({
          buckets: payload.buckets || [],
          menuSize: Number(payload.menuSize) || 0,
          truncated: !!payload.truncated,
          unsaved,
          draftName,
        });
      } catch (err) {
        setPreview(null);
        setPreviewError(readError(err, "Could not preview the routing."));
      } finally {
        setPreviewLoading(false);
      }
    },
    [params]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    setLoadErrorCode(null);
    try {
      const { data } = await apiKitchenStations(params);
      const list = data?.data?.stations || [];
      setStations(list);
      // Not awaited: the cards should paint immediately and the preview, which
      // walks the whole menu, can arrive a beat later.
      runPreview(list);
    } catch (err) {
      setStations([]);
      setPreview(null);
      setLoadErrorCode(readErrorCode(err));
      setLoadError(readError(err, "Could not load the stations."));
    } finally {
      setLoading(false);
    }
  }, [params, runPreview]);

  useEffect(() => {
    load();
  }, [load]);

  const inactiveCount = useMemo(
    () => stations.filter((s) => s?.isActive === false).length,
    [stations]
  );
  const everythingOff = stations.length > 0 && routableStations(stations).length === 0;

  const openNew = () => {
    setEditing(emptyStation());
    setFormOpen(true);
  };

  const openEdit = (station) => {
    setEditing({ ...station, sortOrder: station.sortOrder ?? 100 });
    setFormOpen(true);
  };

  const handleSave = async (form) => {
    setSaving(true);
    try {
      const body = stationToBody(form);
      if (form._id) await apiKitchenUpdateStation(form._id, body, params);
      else await apiKitchenCreateStation(body, params);

      toast.success(form._id ? `${body.name} updated.` : `${body.name} created.`);
      setFormOpen(false);
      setEditing(null);
      // Reload rather than patch locally: setting isDefault clears the flag on
      // whichever station held it, and only the server knows which that was.
      await load();
    } catch (err) {
      toast.error(readError(err, "Could not save that station."));
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (station) => {
    try {
      const body = stationToBody(station, { isActive: station.isActive === false });
      await apiKitchenUpdateStation(station._id, body, params);
      await load();
    } catch (err) {
      toast.error(readError(err, "Could not change that station."));
    }
  };

  const handleDelete = async () => {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await apiKitchenDeleteStation(deleting._id, params);
      toast.success(`${deleting.name} removed.`);
      setDeleting(null);
      await load();
    } catch (err) {
      /* The server refuses to delete the main line with 409
         CANNOT_DELETE_FALLBACK and its message explains exactly why. Showing
         that verbatim beats any generic sentence this screen could invent. */
      setDeleteError(readError(err, "Could not remove that station."));
    } finally {
      setDeleteBusy(false);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    try {
      await apiKitchenSeedStations({}, params);
      toast.success("Starter stations created.");
      await load();
    } catch (err) {
      toast.error(readError(err, "Could not create the starter stations."));
    } finally {
      setSeeding(false);
    }
  };

  const previewDraft = (form) => {
    /* Normalised through the same body builder the save uses, so the preview
       answers for exactly what would be written. The raw form differs in one
       way that matters: a cleared sort order is "" here and 100 on save, and
       "" sorts a station FIRST rather than last. */
    const draft = { ...form, ...stationToBody(form) };
    const merged = form._id
      ? stations.map((s) => (String(s._id) === String(form._id) ? { ...s, ...draft } : s))
      : [...stations, draft];

    /* The unedited form is what goes back into state, not the normalised
       draft: "Back to editing" must reopen what the manager typed, and a draft
       carrying an invented _id would make a NEW station look like an edit and
       send that id to the update route. */
    setEditing(form);
    setFormOpen(false);
    runPreview(merged, { unsaved: true, draftName: draft.name || draft.code });
  };

  /* Drop any unsaved draft and show what is actually stored. Both the
     "Run preview" and "Discard the draft" buttons land here, because they are
     the same act described from two directions. */
  const previewSavedRules = () => {
    setEditing(null);
    runPreview(stations);
  };

  const noStations = !loading && !loadError && stations.length === 0;
  /* Only the very first fetch gets a full page spinner. Later reloads (an
     active toggle, a save) keep the cards on screen, because replacing the
     grid with a spinner for half a second on every switch reads as a fault. */
  const firstLoad = loading && stations.length === 0;

  return (
    <Container fluid style={{ background: THEME.bg, minHeight: "100%", padding: "20px 16px 48px" }}>
      <div
        style={{
          background: `linear-gradient(135deg, ${THEME.header}, ${THEME.headerTo})`,
          borderRadius: THEME.radius,
          boxShadow: THEME.headerShadow,
          color: "#fff",
          padding: "18px 22px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div style={{ fontSize: 20, fontWeight: 700 }}>Kitchen stations</div>
          <div style={{ fontSize: 13, opacity: 0.85, marginTop: 4 }}>
            Which part of the kitchen cooks what, and the rules that decide it.
            {vendorId ? " Viewing one restaurant by request." : ""}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          {kitchens.length > 1 ? (
            <AppSelect block
              size="sm"
              value={vendorId}
              onChange={(e) =>
                history.replace(
                  e.target.value
                    ? `/admin/kitchen-stations?vendorId=${encodeURIComponent(e.target.value)}`
                    : "/admin/kitchen-stations"
                )
              }
              style={{ minWidth: 220 }}
              aria-label="Restaurant"
            >
              <option value="">Choose a restaurant</option>
              {kitchens.map((k) => (
                <option key={k.vendorId} value={k.vendorId}>
                  {k.name}
                </option>
              ))}
            </AppSelect>
          ) : null}
          <Button variant="light" size="sm" onClick={load} disabled={loading}>
            <FiRefreshCw size={13} style={{ marginRight: 6, verticalAlign: "-1px" }} />
            Refresh
          </Button>
          <Button variant="primary" size="sm" onClick={openNew} disabled={loading || !!loadError}>
            <FiPlus size={14} style={{ marginRight: 6, verticalAlign: "-1px" }} />
            New station
          </Button>
        </div>
      </div>

      {firstLoad ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "40px 4px" }}>
          <Spinner animation="border" size="sm" />
          <span style={{ fontSize: 13.5, color: THEME.textMid }}>Loading the stations.</span>
        </div>
      ) : null}

      {loadError ? (
        <Card
          style={{
            marginTop: 18,
            border: `1px solid ${THEME.border}`,
            borderRadius: THEME.radius,
            boxShadow: THEME.shadow,
          }}
        >
          <Card.Body>
            <div style={{ fontWeight: 700, color: THEME.text, marginBottom: 6 }}>
              {loadErrorCode === "VENDOR_REQUIRED"
                ? "Choose a restaurant first"
                : "Could not load the stations"}
            </div>
            <div style={{ fontSize: 13.5, color: THEME.textMid }}>
              {loadError}
              {loadErrorCode === "VENDOR_REQUIRED" ? (
                <>
                  {" "}
                  Stations are configured one kitchen at a time. Pick a restaurant from the
                  selector above. A restaurant signing in with its own account has only one
                  kitchen and never sees that control.
                </>
              ) : null}
            </div>
            <Button variant="outline-primary" size="sm" style={{ marginTop: 12 }} onClick={load}>
              Try again
            </Button>
          </Card.Body>
        </Card>
      ) : null}

      {noStations ? (
        <Card
          style={{
            marginTop: 18,
            border: `1px solid ${THEME.border}`,
            borderRadius: THEME.radius,
            boxShadow: THEME.shadow,
          }}
        >
          <Card.Body style={{ padding: "28px 26px", maxWidth: 780 }}>
            <div style={{ fontSize: 17, fontWeight: 700, color: THEME.text }}>
              No stations yet, so the whole menu is on one board
            </div>
            <div style={{ fontSize: 14, color: THEME.textMid, marginTop: 10, lineHeight: 1.6 }}>
              Right now every item on every ticket lands on the main line. That works, and for a
              small kitchen it is often the right answer. It stops working when the grill and the
              drinks fridge are two different people: they read the same wall of tickets, each
              hunting for the three lines that are theirs.
            </div>
            <div style={{ fontSize: 14, color: THEME.textMid, marginTop: 10, lineHeight: 1.6 }}>
              The starter set creates a grill, a fryer, a cold section, drinks, a packing pass and
              the main line, each with sensible keywords already filled in. Nothing is locked in:
              rename them, switch them off, or change a keyword and watch the preview move.
            </div>
            <Button
              variant="primary"
              style={{ marginTop: 18 }}
              onClick={handleSeed}
              disabled={seeding}
            >
              {seeding ? "Creating" : "Set up starter stations"}
            </Button>
          </Card.Body>
        </Card>
      ) : null}

      {!loadError && stations.length > 0 ? (
        <>
          <Row className="g-3" style={{ marginTop: 6 }}>
            {stations.map((station) => (
              <Col key={station._id} xl={3} lg={4} md={6}>
                <StationCard
                  station={station}
                  stations={stations}
                  busy={saving || deleteBusy}
                  onEdit={openEdit}
                  onDelete={(s) => {
                    setDeleting(s);
                    setDeleteError(null);
                  }}
                  onToggleActive={handleToggleActive}
                />
              </Col>
            ))}
          </Row>

          <div style={{ marginTop: 22 }}>
            <RoutingPreview
              preview={preview}
              loading={previewLoading}
              error={previewError}
              stations={stations}
              hiddenCount={inactiveCount}
              everythingOff={everythingOff}
              onRefresh={previewSavedRules}
              onBackToDraft={() => setFormOpen(true)}
              onDiscardDraft={previewSavedRules}
            />
          </div>
        </>
      ) : null}

      {formOpen && editing ? (
        <StationFormModal
          key={editing._id || "new"}
          station={editing}
          saving={saving}
          onCancel={() => {
            setFormOpen(false);
            setEditing(null);
          }}
          onSave={handleSave}
          onPreviewDraft={previewDraft}
        />
      ) : null}

      <Modal show={!!deleting} onHide={() => setDeleting(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Remove {deleting?.name}?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {deleting && isFallbackStation(deleting) ? (
            <div style={{ fontSize: 13.5, color: THEME.textMid, lineHeight: 1.6 }}>
              The main line is where every unrouted item lands, so it cannot be removed. If it is
              in the way, switch off the stations you are not using instead, or move its keywords
              to another station.
            </div>
          ) : (
            <div style={{ fontSize: 13.5, color: THEME.textMid, lineHeight: 1.6 }}>
              Items that route here will fall to the default station from the next ticket onwards.
              Tickets already cooked keep the station code they were routed with, on purpose:
              rewriting them would erase where the food was actually made.
            </div>
          )}

          {deleteError ? (
            <div
              style={{
                marginTop: 14,
                padding: "10px 12px",
                borderRadius: THEME.radiusSm,
                background: THEME.redSoft,
                border: `1px solid ${THEME.red}`,
                color: THEME.red,
                fontSize: 13,
              }}
            >
              {deleteError}
            </div>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setDeleting(null)} disabled={deleteBusy}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleDelete}
            disabled={deleteBusy || (deleting ? isFallbackStation(deleting) : true)}
          >
            {deleteBusy ? "Removing" : "Remove station"}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default KitchenStations;
